import React from 'react'
import * as Sentry from '@sentry/react'
import { createRoot, hydrateRoot } from 'react-dom/client'
import App from './App.tsx'
import PartLandingPage from './components/part/PartLandingPage.tsx'
import { parsePartLandingBootstrap } from './lib/part-landing.ts'
import './index.css'
import { Analytics } from "@vercel/analytics/react";
import { inject, pageview } from "@vercel/analytics";
import { validateClientEnv, getEnvInfo } from "~/src/lib/env-validator";
import { ErrorBoundary } from "~/src/components/ErrorBoundary";
import { debugLog } from "~/src/lib/debug";
import { analyticsAllowed, privacySafeAnalyticsEvent } from "~/src/lib/analytics-privacy";

// Initialize Sentry (no-op if DSN missing)
try {
  const dsn = (import.meta as any)?.env?.VITE_SENTRY_DSN as string | undefined
  if (dsn) {
    Sentry.init({
      dsn,
      environment: ((import.meta as any)?.env?.VITE_SENTRY_ENVIRONMENT as string) || ((import.meta as any)?.env?.MODE as string) || 'production',
      integrations: [
        // Prefer v8 API if available
        typeof (Sentry as any).browserTracingIntegration === 'function' ? (Sentry as any).browserTracingIntegration() : undefined,
      ].filter(Boolean),
      // Keep low by default; adjust via VITE_SENTRY_TRACES_SAMPLE_RATE
      tracesSampleRate: Number(((import.meta as any)?.env?.VITE_SENTRY_TRACES_SAMPLE_RATE as string) ?? '0.1'),
      release: ((import.meta as any)?.env?.VITE_SENTRY_RELEASE as string) || undefined,
    })
    Sentry.setTag('app', 'web')
  }
} catch (err) {
  // Safe fallback if Sentry init fails
  console.warn('Sentry init failed:', err)
}

// Validate environment variables at startup
try {
  validateClientEnv();
  const envInfo = getEnvInfo();
  if (import.meta.env?.DEV) {
    debugLog('🚀 Environment validated for development');
  }
} catch (error) {
  console.error('❌ Environment validation failed:', error);
  if (import.meta.env?.PROD) {
    // Show user-friendly error in production
    document.body.innerHTML = `
      <div style="display: flex; justify-content: center; align-items: center; height: 100vh; font-family: Arial, sans-serif;">
        <div style="text-align: center; max-width: 500px; padding: 20px;">
          <h1 style="color: #ef4444;">Configuration Error</h1>
          <p>The application is not properly configured. Please contact support.</p>
          <p style="font-size: 0.9em; color: #6b7280; margin-top: 20px;">Error ID: ${Date.now()}</p>
        </div>
      </div>
    `;
    throw error;
  }
}

const rootElement = document.getElementById("root")!;
const partBootstrap = parsePartLandingBootstrap(
  document.getElementById("part-landing-data")?.textContent,
);

if (partBootstrap) {
  // The server route renders this exact, router-free component. No wrapper is
  // introduced here so hydrateRoot sees identical server/client markup.
  hydrateRoot(rootElement, <PartLandingPage model={partBootstrap} enableLiveRefresh />, {
    onRecoverableError(error) {
      console.error("Part landing hydration recovered from an error", error);
      if (import.meta.env?.PROD) Sentry.captureException(error);
    },
  });
  // This standalone hydrated tree intentionally has no wrapper. Injecting the
  // analytics script after hydrate preserves byte-for-byte SSR parity.
  inject({ framework: "react", disableAutoTrack: true, beforeSend: privacySafeAnalyticsEvent });
  if (analyticsAllowed()) pageview({ route: "/parts/[slug]", path: window.location.pathname });
} else {
  createRoot(rootElement).render(
    <React.StrictMode>
      <ErrorBoundary
        onError={(error, errorInfo) => {
          // Custom error reporting logic
          console.error('🚨 Global Error Boundary:', { error, errorInfo });
          
          if (import.meta.env?.PROD) {
            Sentry.captureException(error, { extra: errorInfo as any });
          }
        }}
      >
        <App />
        <Analytics beforeSend={privacySafeAnalyticsEvent} />
      </ErrorBoundary>
    </React.StrictMode>
  );
}
